import { gql } from '@apollo/client';

/**
 * ТЗ 0076 — полная карточка документа на подпись.
 *
 * Один фрагмент на карточку и на КАЖДУЮ мутацию, которая возвращает
 * `contract`: ответ мутации обязан обновить ту же запись кэша, из которой
 * читает ящик «Договор», — иначе статус после «Отправить» остаётся старым до
 * следующего опроса.
 *
 * 🔴 Ссылки на подпись здесь НЕТ и быть не может: в типе её нет (бэкенд
 * отдаёт её только мутацией `revealContractSigningLink`). `previewPdfUrl` и
 * `signedPdfUrl` — presigned на 5 минут, поэтому для открытия PDF они
 * перечитываются отдельным запросом без кэша, а не берутся отсюда.
 */
export const ESIGN_CONTRACT_CARD_FIELDS = gql`
  fragment EsignContractCardFields on ContractDocumentType {
    id
    uuid
    kind
    number
    status
    fulfilmentStatus
    fulfilmentError
    fulfilmentCandidates {
      id
      name
      isStudentHere
    }
    cancelConflict
    textPending
    signerFirstName
    signerLastName
    signerEmail
    signerPhone
    groupId
    groupName
    courseName
    paymentPlan {
      id
      code
      name
      installments
      firstDueKind
      firstDueDays
      marksStatePrefinanced
    }
    currency
    listPrice
    price
    schedule {
      n
      amount
      rule
      ruleKind
      ruleParams
    }
    company {
      name
      taxId
      regon
      address
      paymentModel
      invoiceTo
      pricePerPerson
      totalPrice
      participants {
        firstName
        lastName
        email
        phone
      }
    }
    teacherId
    applicationId
    applicationTitle
    studentId
    agreementId
    teacherAgreementId
    existingUserId
    createdById
    createdByName
    templateId
    templateCode
    templateName
    templateAppendix
    sellerId
    sellerName
    sellerLocked
    sentById
    sentByName
    canSeeCorrespondence
    canManage
    emailFrom
    inviteMailbox
    inviteMailboxKind
    inviteBlockedReason
    createdAt
    sentAt
    emailSentAt
    emailDeliveredAt
    emailOpenedAt
    emailBouncedAt
    emailSendCount
    emailError {
      code
      message
      occurredAt
    }
    openedAt
    signedAt
    completedAt
    rejectedAt
    rejectionReason
    cancelledAt
    cancelReason
    expiresAt
    sendError
    signerLegalName
    signerTaxId
    signerAddress
    signerPesel
    crmLeadId
    crmLeadUrl
    # ТЗ 0090: лид договора в зеркале Kommo — ссылка «Карточка лида» и его
    # лесенки в окне договора. Только карточка: в строке списка это поле
    # стоило бы запроса на строку. FinanceManager и HR получают null.
    crmLead {
      id
      kommoId
      personName
      name
      isClosed
      kommoUrl
    }
    requisiteWarnings
    consents
    signerFields {
      key
      label
      value
      checked
    }
    events {
      id
      source
      event
      occurredAt
      actorName
      details
    }
  }
`;

/**
 * ТЗ 0077 — SMS-напоминания по договору студента.
 *
 * Один фрагмент на запрос ящика и на КАЖДУЮ мутацию «Напомнить сейчас» /
 * «Остановить» / «Возобновить»: у `ContractOutreachType` нет `id`, кэш его не
 * нормализует, и ответ мутации записывается в запрос ящика целиком
 * (`writeQuery`) — форма ответа обязана совпадать с формой запроса.
 *
 * 🔴 Ни короткой ссылки, ни ссылки на подпись здесь нет и быть не может: обе
 * ведут на подпись, и бэкенд их наружу не отдаёт. Экрану хватает счётчиков.
 */
export const ESIGN_OUTREACH_FIELDS = gql`
  fragment EsignOutreachFields on ContractOutreachType {
    available
    reason
    stopped
    autoReminders
    nextStepAt
    nextStepCondition
    nextStepReason
    state
    messages {
      id
      step
      stepLabel
      condition
      status
      skipReason
      scheduledFor
      createdAt
      sentAt
      deliveredAt
      dlrStatus
      segments
      phoneMasked
      body
      templateCode
      templateName
      templateNamePl
      createdByName
      error
    }
    link {
      clickCount
      humanClickCount
      firstHumanClickedAt
      lastClickedAt
      expiresAt
      revoked
    }
  }
`;

/**
 * ТЗ 0078 — шаблон SMS или письма из справочника.
 *
 * ТЗ 0084: `usedIn`/`usedInPl` — где код называет шаблон (шаг лесенки,
 * «Напомнить сейчас», письмо-приглашение); такой шаблон не выключить.
 *
 * Один фрагмент на список и на ответ `saveMessageTemplate`: у типа есть `id`,
 * кэш его нормализует, и правка шаблона обновляет строку списка из ответа.
 */
export const ESIGN_MESSAGE_TEMPLATE_FIELDS = gql`
  fragment EsignMessageTemplateFields on MessageTemplateType {
    id
    channel
    code
    name
    namePl
    subject
    body
    isSystem
    isActive
    updatedAt
    updatedByName
    usedIn
    usedInPl
  }
`;
